import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import GalleryPage from "./pages/GalleryPage";
import TrainingsPage from "./pages/TrainingsPage";
import ServicesPage from "./pages/ServicesPage";
import ContactPage from "./pages/ContactPage";
import DocumentairePage from "./pages/DocumentairePage";
import TrainersPage from "./pages/TrainersPage";
import PartnersPage from "./pages/PartnersPage";
import EventsPage from "./pages/EventsPage";
import ReviewsPage from "./pages/ReviewsPage";
import RadioPage from "./pages/RadioPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import TrainingDetail from "./pages/TrainingDetail";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/sonner";

// Détection automatique de l'URL backend selon l'environnement
const getBackendUrl = () => {
  // Si on est sur le domaine de production, utiliser l'URL de production
  if (window.location.hostname === 'stuntdirector.fr' || window.location.hostname === 'www.stuntdirector.fr') {
    return 'https://stuntdirector.fr';
  }
  // Sinon, utiliser la variable d'environnement (prévisualisation)
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Analytics Tracker Component
const AnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Ne pas tracker les pages admin
    if (location.pathname.startsWith('/admin')) {
      return;
    }
    
    // Track page view
    const trackPageView = async () => {
      try {
        await axios.post(`${API}/analytics/track`, null, {
          params: {
            page: location.pathname,
            referrer: document.referrer || null
          }
        });
      } catch (error) {
        // Silent fail - don't break the app if tracking fails
        console.debug('Analytics tracking failed:', error);
      }
    };
    
    trackPageView();
  }, [location.pathname]);
  
  return null;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Simple token encoding (obfuscation) - not encryption but better than plain text
const encodeToken = (token) => {
  try {
    return btoa(encodeURIComponent(token));
  } catch {
    return token;
  }
};

const decodeToken = (encoded) => {
  try {
    return decodeURIComponent(atob(encoded));
  } catch {
    return encoded;
  }
};

const getStoredToken = () => {
  const encoded = sessionStorage.getItem('admin_token');
  return encoded ? decodeToken(encoded) : null;
};

const setStoredToken = (token) => {
  if (token) {
    sessionStorage.setItem('admin_token', encodeToken(token));
  } else {
    sessionStorage.removeItem('admin_token');
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getStoredToken);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyAuth = async () => {
      const currentToken = token;
      if (currentToken) {
        try {
          await axios.get(`${API}/auth/verify`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          setIsAuthenticated(true);
        } catch {
          setStoredToken(null);
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };
    verifyAuth();
  }, [token]);

  const login = (newToken) => {
    setStoredToken(newToken);
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    setStoredToken(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Layout with Navbar and Footer
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-black">
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
            <Route path="/trainings" element={<PublicLayout><TrainingsPage /></PublicLayout>} />
            <Route path="/trainings/:id" element={<PublicLayout><TrainingDetail /></PublicLayout>} />
            <Route path="/formateurs" element={<PublicLayout><TrainersPage /></PublicLayout>} />
            <Route path="/partenaires" element={<PublicLayout><PartnersPage /></PublicLayout>} />
            <Route path="/evenements" element={<PublicLayout><EventsPage /></PublicLayout>} />
            <Route path="/avis" element={<PublicLayout><ReviewsPage /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/documentaire" element={<PublicLayout><DocumentairePage /></PublicLayout>} />
            <Route path="/radio" element={<PublicLayout><RadioPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/payment/success" element={<PublicLayout><PaymentSuccess /></PublicLayout>} />
            <Route path="/payment/cancel" element={<PublicLayout><PaymentCancel /></PublicLayout>} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
